/**
 * @component group-settings-rss
 * @css group-settings-rss.css
 */
import { escapeHtml } from '../utils/html-utils.js';
import { appendReturnTo } from '../utils/return-to-url.js';
import { canAddGroupRssFeeds, MOD_ROLES } from '../utils/group-invite-policy.js';

class GroupSettingsRss extends HTMLElement {
  constructor() {
    super();
    this.groupId = null;
    this.group = null;
    this.membership = null;
    this.threads = [];
    this.feeds = [];
    this.showAddForm = false;
    this.loading = true;
  }

  setContext({ groupId, group, membership, threads = [] }) {
    this.groupId = groupId;
    this.group = group;
    this.membership = membership;
    this.threads = threads.filter((t) => !t.archived_at);
    this.load();
  }

  canAdd() {
    return canAddGroupRssFeeds(this.group, this.membership);
  }

  isMod() {
    return MOD_ROLES.includes(this.membership?.role);
  }

  async load() {
    if (!window.API || !this.groupId) return;
    this.loading = true;
    this.render();
    try {
      this.feeds = await window.API.request(`/groups/${this.groupId}/rss`);
    } catch (e) {
      window.logger?.warn('Failed to load group RSS feeds', e);
      this.feeds = [];
    } finally {
      this.loading = false;
      this.render();
      this.attachListeners();
    }
  }

  renderThreadOptions(selectedId) {
    const mainSelected = selectedId === null || selectedId === undefined ? 'selected' : '';
    let html = `<option value="" ${mainSelected}>Main stream</option>`;
    for (const thread of this.threads) {
      const sel = String(thread.id) === String(selectedId) ? 'selected' : '';
      html += `<option value="${thread.id}" ${sel}>${escapeHtml(thread.name)}</option>`;
    }
    return html;
  }

  renderDestination(feed) {
    if (!feed.topic_id) return '📡 Main stream';
    return `🧵 ${escapeHtml(feed.topic_name || 'Thread')}`;
  }

  render() {
    const canAdd = this.canAdd();
    this.innerHTML = `
      <section class="settings-group group-settings-rss">
        <div class="group-header">
          <h3>📡 RSS Feeds</h3>
          <p class="group-description">Import posts from external blogs into this group.
            <a href="${appendReturnTo('/docs/rss/group-feeds')}">Group RSS help →</a></p>
        </div>
        ${canAdd ? '<button type="button" class="btn btn-primary" id="groupRssAddBtn">+ Add Feed</button>' : ''}
        ${this.showAddForm && canAdd ? `
          <form id="groupRssAddForm" class="add-feed-form">
            <div class="form-group">
              <label for="groupFeedUrl">Feed URL</label>
              <input type="url" id="groupFeedUrl" class="form-control" required placeholder="https://example.com/feed.xml" />
            </div>
            <div class="form-group">
              <label for="groupFeedTopic">Post to</label>
              <select id="groupFeedTopic" class="form-control">${this.renderThreadOptions(null)}</select>
            </div>
            <div class="form-group">
              <label class="checkbox-label">
                <input type="checkbox" id="groupFeedNotify" />
                Email group members when this feed updates
              </label>
            </div>
            <div class="form-actions">
              <button type="submit" class="btn btn-primary">Add Feed</button>
              <button type="button" class="btn btn-secondary" id="groupRssCancelBtn">Cancel</button>
            </div>
          </form>
        ` : ''}
        ${this.loading ? '<div class="loading-state"><div class="spinner"></div></div>' : ''}
        ${!this.loading && this.feeds.length === 0 ? '<p class="empty-hint">No RSS feeds yet.</p>' : ''}
        ${!this.loading && this.feeds.length ? `
          <div class="feeds-list">
            ${this.feeds.map((feed) => `
              <div class="feed-item" data-feed-id="${feed.id}">
                <div class="feed-header">
                  <h4>${escapeHtml(feed.title || feed.feed_url)}</h4>
                  <span class="feed-destination">${this.renderDestination(feed)}</span>
                </div>
                <p class="feed-url">${escapeHtml(feed.discovered_feed_url || feed.feed_url)}</p>
                <div class="feed-actions">
                  ${feed.status === 'active'
    ? `<button type="button" class="btn btn-sm btn-secondary pause-btn" data-feed-id="${feed.id}">Pause</button>`
    : `<button type="button" class="btn btn-sm btn-primary resume-btn" data-feed-id="${feed.id}">Resume</button>`}
                  ${this.isMod() ? `<button type="button" class="btn btn-sm btn-danger delete-btn" data-feed-id="${feed.id}">Delete</button>` : ''}
                </div>
              </div>
            `).join('')}
          </div>
        ` : ''}
      </section>
    `;
  }

  attachListeners() {
    this.querySelector('#groupRssAddBtn')?.addEventListener('click', () => {
      this.showAddForm = true;
      this.render();
      this.attachListeners();
    });
    this.querySelector('#groupRssCancelBtn')?.addEventListener('click', () => {
      this.showAddForm = false;
      this.render();
      this.attachListeners();
    });
    this.querySelector('#groupRssAddForm')?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const url = this.querySelector('#groupFeedUrl')?.value?.trim();
      const topicVal = this.querySelector('#groupFeedTopic')?.value;
      const notify = this.querySelector('#groupFeedNotify')?.checked;
      if (!url) return;
      try {
        await window.API.request(`/groups/${this.groupId}/rss`, {
          method: 'POST',
          body: JSON.stringify({
            url,
            topic_id: topicVal ? parseInt(topicVal, 10) : null,
            notify_members_email: Boolean(notify)
          })
        });
        this.showAddForm = false;
        await this.load();
      } catch (err) {
        window.alert(err.message || 'Failed to add feed');
      }
    });
    this.querySelectorAll('.pause-btn, .resume-btn, .delete-btn').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const feedId = btn.dataset.feedId;
        try {
          if (btn.classList.contains('delete-btn')) {
            if (!confirm('Delete this RSS feed?')) return;
            await window.API.request(`/groups/${this.groupId}/rss/${feedId}`, { method: 'DELETE' });
          } else {
            const status = btn.classList.contains('pause-btn') ? 'paused' : 'active';
            await window.API.request(`/groups/${this.groupId}/rss/${feedId}`, {
              method: 'PATCH',
              body: JSON.stringify({ status })
            });
          }
          await this.load();
        } catch (err) {
          window.alert(err.message || 'Action failed');
        }
      });
    });
  }
}

customElements.define('group-settings-rss', GroupSettingsRss);
